import { useState } from 'react';
import { toast } from 'sonner';
import { FolderSimplePlus } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { dmsApi } from './dmsApi';
import { folderOptions, folderPathLabel } from './folderTree';

const TITLES = { rename: 'フォルダー名を変更', move: 'フォルダーを移動', share: '共有・権限' };

export default function FolderDialog({ folders, editFolder, mode, defaultParentId, onClose, onSaved }) {
  const [name, setName] = useState(editFolder?.name || '');
  const [parentId, setParentId] = useState(editFolder?.parent_folder_id || defaultParentId || '');
  const [scope, setScope] = useState(editFolder?.view_scope || 'engineering');
  const [description, setDescription] = useState(editFolder?.description || '');
  const [saving, setSaving] = useState(false);
  // 自分自身と配下は移動先にできない（自分の中に自分を入れると木が壊れる）
  const parentChoices = folderOptions(folders, { excludeSubtreeOf: editFolder?.id });

  const save = async () => {
    if (!name.trim()) { toast.error('フォルダー名を入力してください'); return; }
    setSaving(true);
    try {
      const body = {
        name: name.trim(), parent_folder_id: parentId || null,
        description: description.trim() || null, view_scope: scope,
        allowed_user_ids: editFolder?.allowed_user_ids || [],
        is_archive_folder: !!editFolder?.is_archive_folder,
      };
      if (editFolder) await dmsApi.updateFolder(editFolder.id, body);
      else await dmsApi.createFolder(body);
      toast.success(editFolder ? 'フォルダーを更新しました' : 'フォルダーを作成しました');
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'フォルダーの保存に失敗しました');
    } finally { setSaving(false); }
  };

  // 操作ごとに必要な項目だけを見せる（名前を変更／移動／共有・権限）
  const FIELDS = {
    rename: ['name'], move: ['parent'], share: ['scope'],
  };
  const show = (f) => !FIELDS[mode] || FIELDS[mode].includes(f);

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-md" data-testid="dms-folder-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <FolderSimplePlus size={20} weight="duotone" className="text-teal-600" />
            {editFolder ? (TITLES[mode] || 'フォルダー編集') : '新規フォルダー'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className={show('name') ? '' : 'hidden'}>
            <Label className="text-xs font-semibold text-zinc-600">フォルダー名 <span className="text-rose-500">＊</span></Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120}
              autoFocus={mode !== 'move' && mode !== 'share'}
              className="h-9 mt-1" data-testid="dms-folder-name" />
          </div>
          <div className={show('parent') ? '' : 'hidden'}>
            <Label className="text-xs font-semibold text-zinc-600">親フォルダー（このフォルダーの置き場所）</Label>
            <select value={parentId} onChange={(e) => setParentId(e.target.value)} data-testid="dms-folder-parent"
              className="mt-1 h-9 w-full rounded-md border border-zinc-300 bg-white px-2 text-sm outline-none focus:ring-2 focus:ring-teal-200">
              <option value="">（ルート：いちばん上）</option>
              {parentChoices.map((o) => (
                <option key={o.value} value={o.value} title={o.pathLabel}>{o.label}</option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-zinc-500" data-testid="dms-folder-parent-path">
              移動先: {parentId
                ? `${folderPathLabel(folders, parentId)} / ${name || '（このフォルダー）'}`
                : `（ルート） / ${name || '（このフォルダー）'}`}
            </p>
          </div>
          <div className={!show('scope') ? 'hidden'
            : mode === 'share' ? 'rounded-md border border-teal-200 bg-teal-50/50 p-2.5' : ''}>
            <Label className="text-xs font-semibold text-zinc-600">閲覧範囲（このフォルダーを見られる人）</Label>
            <select value={scope} onChange={(e) => setScope(e.target.value)} data-testid="dms-folder-scope"
              className="mt-1 h-9 w-full rounded-md border border-zinc-300 bg-white px-2 text-sm outline-none focus:ring-2 focus:ring-teal-200">
              <option value="engineering">技術メンバーのみ</option>
              <option value="all">全社公開（アプリ利用者全員）</option>
              <option value="custom">指定メンバーのみ（管理者が設定）</option>
            </select>
          </div>
          <div className={show('desc') ? '' : 'hidden'}>
            <Label className="text-xs font-semibold text-zinc-600">説明（任意）</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={500}
              className="h-9 mt-1" data-testid="dms-folder-desc" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="dms-folder-cancel">キャンセル</Button>
          <Button onClick={save} disabled={saving} className="bg-teal-700 hover:bg-teal-800" data-testid="dms-folder-save">
            {saving ? '保存中...' : '保存'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
